import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import { GLYPHS } from '@/components/ui';
import { Employee, WorkSchedule } from '../../types/employee';
import { getWeeklyHolidayAllowanceDetails } from '../../utils/payrollCalculations';

interface WeeklyHolidayAllowanceDialogProps {
  open: boolean;
  onClose: () => void;
  employee: Employee;
  schedules: WorkSchedule[];
  selectedMonth: Date;
}

const WeeklyHolidayAllowanceDialog: React.FC<WeeklyHolidayAllowanceDialogProps> = ({
  open,
  onClose,
  employee,
  schedules,
  selectedMonth,
}) => {
  // 주간별 주휴수당 상세 정보 계산
  const weeklyDetails = useMemo(() => {
    if (!employee.isHourly || !schedules.length) return [];
    
    // 해당 직원의 스케줄만 필터링
    const employeeSchedules = schedules.filter(s => s.employee_id === employee.id);
    return getWeeklyHolidayAllowanceDetails(employeeSchedules, employee, selectedMonth);
  }, [employee, schedules, selectedMonth]);

  // 총 주휴수당 계산
  const totalHolidayAllowance = useMemo(() => {
    return weeklyDetails.reduce((sum, week) => sum + week.holidayAllowance, 0);
  }, [weeklyDetails]);

  // 주휴수당 지급 주차 수
  const eligibleWeeksCount = useMemo(() => {
    return weeklyDetails.filter(week => week.isEligible).length;
  }, [weeklyDetails]);

  if (!employee.isHourly) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              주휴수당 계산
            </Typography>
            <IconButton onClick={onClose} size="small" aria-label="닫기"
              sx={{ fontSize: 22, lineHeight: 1, color: 'var(--fg-secondary)' }}>
              {GLYPHS.close}
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info">
            {employee.name}님은 월급제 직원으로 주휴수당이 별도로 계산되지 않습니다.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>닫기</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              주휴수당 계산 상세
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {employee.name} - {selectedMonth.getFullYear()}년 {selectedMonth.getMonth() + 1}월
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="닫기"
            sx={{ fontSize: 22, lineHeight: 1, color: 'var(--fg-secondary)' }}>
            {GLYPHS.close}
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* 계산 방식 안내 */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            주휴수당 계산 방식
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            • 주 단위: 일요일 ~ 토요일 (7일)
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            • 지급 조건: 주당 근무시간 15시간 이상
          </Typography>
          <Typography variant="body2">
            • 계산식: (주당 근무시간 ÷ 40) × 8시간 × 시급({employee.hourlyRate.toLocaleString()})
          </Typography>
        </Alert>

        {/* 요약 카드 */}
        <Card sx={{ mb: 3, background: 'var(--primary)', color: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                  {selectedMonth.getMonth() + 1}월 총 주휴수당
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }} className="num">{totalHolidayAllowance.toLocaleString()}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                  지급 주차 수
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {eligibleWeeksCount}주차
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* 주차별 상세 정보 */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          주차별 상세 내역
        </Typography>

        {weeklyDetails.length === 0 ? (
          <Alert severity="warning">
            해당 월에 등록된 근무 스케줄이 없습니다.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {weeklyDetails.map((week) => (
              <Card 
                key={week.weekNumber} 
                sx={{ 
                  border: week.isEligible ? '2px solid var(--primary)' : '1px solid var(--border-default)',
                  boxShadow: week.isEligible ? 'var(--shadow-1)' : 'none'
                }}
              >
                <CardContent>
                  {/* 주차 헤더 */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {selectedMonth.getMonth() + 1}월 {week.weekNumber}주차
                      </Typography>
                      <Chip 
                        label={`일 ${week.weekStartFormatted} ~ 토 ${week.weekEndFormatted}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    {week.isEligible ? (
                      <Chip
                        label="지급"
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Chip
                        label="미지급"
                        color="default"
                        size="small"
                      />
                    )}
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {/* 근무 시간 및 주휴수당 */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1, p: 2, background: 'var(--bg-elev-1)', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          주당 근무시간
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {week.totalWorkHours}시간
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {week.isEligible ? '15시간 이상 ✓' : '15시간 미만'}
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, p: 2, background: week.isEligible ? 'var(--primary-bg)' : 'var(--bg-elev-1)', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          주휴수당
                        </Typography>
                      </Box>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 600,
                          color: week.isEligible ? '#2e7d32' : 'text.primary'
                        }}
                       className="num">{week.holidayAllowance.toLocaleString()}</Typography>
                      {week.isEligible && (
                        <Typography variant="caption" color="text.secondary">
                          ({week.totalWorkHours} ÷ 40) × 8 × {employee.hourlyRate.toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* 근무일 목록 */}
                  {week.workDays.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'var(--fg-tertiary)' }}>
                        근무일 상세 (총 {week.workDaysCount}일)
                      </Typography>
                      <List dense sx={{ bgcolor: 'var(--bg-elev-1)', borderRadius: 1 }}>
                        {week.workDays.map((workDay, index) => (
                          <ListItem 
                            key={index}
                            sx={{ 
                              borderBottom: index < week.workDays.length - 1 ? '1px solid var(--border-default)' : 'none'
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Typography variant="body2" sx={{ minWidth: 100 }}>
                                    {workDay.date.toLocaleDateString('ko-KR', { 
                                      month: 'short', 
                                      day: 'numeric',
                                      weekday: 'short'
                                    })}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {workDay.timeDisplay || `${workDay.startTime} ~ ${workDay.endTime}`}
                                  </Typography>
                                  <Chip 
                                    label={`${workDay.workHours}시간`}
                                    size="small"
                                    variant="outlined"
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    휴게: {workDay.breakTime}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}

                  {week.workDays.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      이 주차에는 등록된 근무일이 없습니다.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="contained">
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeeklyHolidayAllowanceDialog;

